import { useMemo } from 'react'
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table'

import { DataTable, DataTableProvider } from 'components/DataTable'
import { DataTableContainer } from 'components/DataTable/UI'
import FbButton from 'components/FbUI/FbButton'
import { ActionsDropdown } from 'components/Buttons/ActionsDropdown'
import { TextPill } from 'components/FbUI/TextPill'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { type SenderEmail } from '../sender_emails_api'

const STATUS_LABELS: Record<string, string> = {
  connected: 'Connected',
  not_connected: 'Not Connected',
  pending_move: 'Pending Move',
  pending_deletion: 'Pending Deletion',
}

// DS2026 50/700 palette (matches Rebate status / Sample status pills).
const STATUS_PILL_DS2026: Record<string, { bg: string; text: string }> = {
  connected: { bg: '#dcfce7', text: '#15803d' }, // green
  not_connected: { bg: '#f3f4f6', text: '#374151' }, // gray
  pending_move: { bg: '#fef3c7', text: '#b45309' }, // amber
  pending_deletion: { bg: '#fee2e2', text: '#b91c1c' }, // red
}

// Legacy Tailwind palette pills used outside DS2026 surfaces.
const STATUS_COLORS: Record<string, string> = {
  connected: 'bg-green-100 text-green-800',
  not_connected: 'bg-gray-100 text-gray-800',
  pending_move: 'bg-amber-100 text-amber-800',
  pending_deletion: 'bg-red-100 text-red-800',
}

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  google_workspace_oauth: 'Google',
  microsoft_oauth: 'Microsoft',
  imap_smtp: 'IMAP/SMTP',
  inbox: 'Inbox',
}

// ActionItem interface matching ActionsDropdown
interface ActionItem {
  label: string
  icon: React.ComponentType<{ size?: number; className?: string }>
  onClick: () => void
  variant?: 'default' | 'destructive'
  disabled?: boolean
  disabledReason?: string
  hasSeparatorAfter?: boolean
}

interface SenderEmailsTableProps {
  data: SenderEmail[]
  isLoading: boolean
  tableKey?: string
  // Custom table header (takes precedence over headerActions)
  tableHeader?: React.JSX.Element
  // Header actions (optional - for campaign view, ignored if tableHeader is provided)
  headerActions?: {
    onSync?: () => void
    onAdd?: () => void
    isSyncing?: boolean
  }
  // Row actions - function that returns ActionItem array for each sender email
  getRowActions?: (senderEmail: SenderEmail) => ActionItem[]
}

const columnHelper = createColumnHelper<SenderEmail>()

function SenderEmailsTableContent({
  data,
  isLoading,
  tableKey = 'sender-emails-table',
  tableHeader: customTableHeader,
  headerActions,
  getRowActions,
}: SenderEmailsTableProps) {
  const linkVariant = useTableLinkVariant()
  const isPlain = linkVariant === 'plain'

  const columns = useMemo((): ColumnDef<SenderEmail, any>[] => {
    const baseColumns: ColumnDef<SenderEmail, any>[] = [
      columnHelper.accessor('name', {
        header: 'Sender',
        size: 250,
        meta: isPlain ? { isFirstColumn: true } : undefined,
        cell: (info) => (
          <div>
            <p className="text-sm font-medium text-fg truncate">
              {info.getValue()}
            </p>
            <p className="text-sm text-fg-secondary">
              {info.row.original.email}
            </p>
          </div>
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        size: 140,
        cell: (info) => {
          const status = info.getValue()
          const label = STATUS_LABELS[status] || status
          if (isPlain) {
            const palette =
              STATUS_PILL_DS2026[status] ?? STATUS_PILL_DS2026.not_connected
            return (
              <TextPill backgroundColor={palette.bg} textColor={palette.text}>
                {label}
              </TextPill>
            )
          }
          return (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                STATUS_COLORS[status] || STATUS_COLORS.not_connected
              }`}
            >
              {label}
            </span>
          )
        },
      }),
      columnHelper.accessor('account_type', {
        header: 'Type',
        size: 120,
        cell: (info) => {
          const label = ACCOUNT_TYPE_LABELS[info.getValue()] || info.getValue()
          if (isPlain) {
            return (
              <TextPill backgroundColor="#f3f4f6" textColor="#374151">
                {label}
              </TextPill>
            )
          }
          return <span className="text-sm text-fg-secondary">{label}</span>
        },
      }),
      columnHelper.accessor('daily_limit', {
        header: 'Daily Limit',
        size: 100,
        cell: (info) => (
          <span className="text-sm text-fg-secondary">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor('emails_sent', {
        header: 'Sent',
        size: 70,
        cell: (info) => (
          <span className="text-sm text-fg-secondary">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor('unique_opens', {
        header: 'Opens',
        size: 70,
        cell: (info) => (
          <span className="text-sm text-fg-secondary">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor('unique_replies', {
        header: 'Replies',
        size: 70,
        cell: (info) => (
          <span className="text-sm text-fg-secondary">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor('bounced', {
        header: 'Bounced',
        size: 70,
        cell: (info) => (
          <span className="text-sm text-fg-secondary">{info.getValue()}</span>
        ),
      }),
    ]

    // Add actions column only if getRowActions is provided
    if (getRowActions) {
      baseColumns.push(
        columnHelper.display({
          id: 'actions',
          header: isPlain
            ? () => <div className="w-full text-center">Actions</div>
            : 'Actions',
          size: isPlain ? 70 : 100,
          meta: {
            isLastColumn: true,
            headerDisableOrdering: true,
            rightAlign: !isPlain,
          },
          cell: (info) => {
            const actions = getRowActions(info.row.original)
            if (actions.length === 0) return null
            return (
              <ActionsDropdown
                actions={actions}
                className={isPlain ? '!w-full justify-center' : undefined}
              />
            )
          },
        })
      )
    }

    return baseColumns
  }, [getRowActions, isPlain])

  const tableHeader = useMemo((): React.JSX.Element | undefined => {
    // Use custom table header if provided
    if (customTableHeader !== undefined) return customTableHeader

    // Otherwise, generate header from headerActions
    if (!headerActions) return undefined

    const { onSync, onAdd, isSyncing } = headerActions
    if (!onSync && !onAdd) return undefined

    return (
      <div className="flex justify-end items-center w-full py-2 gap-2">
        {onSync && (
          <FbButton
            onClick={onSync}
            disabled={isSyncing}
            loading={isSyncing}
            variant="secondary"
            size="sm"
          >
            Sync Workspace
          </FbButton>
        )}
        {onAdd && (
          <FbButton onClick={onAdd} variant="primary" size="sm">
            Add Sender Emails
          </FbButton>
        )}
      </div>
    )
  }, [customTableHeader, headerActions])

  return (
    <DataTableContainer>
      <DataTable
        tableKey={tableKey}
        data={data}
        columns={columns}
        tableHeader={tableHeader}
        loading={isLoading}
        stickyLastColumn={isPlain && !!getRowActions}
      />
    </DataTableContainer>
  )
}

export function SenderEmailsTable(props: SenderEmailsTableProps) {
  const tableKey = props.tableKey || 'sender-emails-table'
  return (
    <ColumnsStoreProvider tableKey={tableKey}>
      <DataTableProvider tableKey={tableKey}>
        <SenderEmailsTableContent {...props} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
